import { c as _c } from "react/compiler-runtime";
// A space's board columns (Decision ontology-tasks:statuses-as-data): the statuses it sees, its own and the ones it
// inherits from the spaces above, in board order. Its own: rename, move within their category; inherited: hide or
// show here (and in the spaces below); add one. The rules are the core's (board.columns, board.column); the modal
// ?columns=<space> (layouts/modals.tsx), opened from the filter's Columns. Reorder: drag a row by its grip (pointer
// events, phones too), or its arrows; a colour is one of the swatches or the last one, any colour (native picker).
import { type PointerEvent as ReactPointerEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, type ColumnEdit, type Status } from "@/api";
import { useData } from "@/store";
import { Dot } from "./dot";
import { ErrorLine } from "./error-line";
import { Icon } from "./icon";
import { Field } from "./field";
import { Group, Stack } from "./group";
import { LinkButton } from "./link-button";
import { SectionHead } from "./section-head";
import { Segment, Segmented } from "./segmented";
const CATEGORIES = ["backlog", "unstarted", "started", "completed", "canceled"] as const;
/** The swatches: grey, blue, violet, green, yellow, orange, red, pink (any other: the last swatch's picker). */
const SWATCHES = ["#8a8a8a", "#4f7bd8", "#8a6fd1", "#3f9b6b", "#c79a2a", "#d97a3a", "#d0574e", "#c95c9a"];
export function Columns(t0) {
  const $ = _c(94);
  const {
    space
  } = t0;
  const {
    t
  } = useTranslation();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [cols, setCols] = useState(t1);
  const [error, setError] = useState(null);
  const [renaming, setRenaming] = useState(null);
  const [name, setName] = useState("");
  const [category, setCategory] = useState("unstarted");
  const [color, setColor] = useState("#8a8a8a");
  const column = useData(_temp);
  const projects = useData(_temp2);
  let t2;
  if ($[1] !== projects) {
    t2 = id => projects?.find(p => p.id === id)?.name ?? id;
    $[1] = projects;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const spaceName = t2;
  let t3;
  let t4;
  if ($[3] !== space) {
    t3 = () => api.board.columns(space, (v, e) => v ? setCols(v) : setError(e));
    t4 = [space];
    $[3] = space;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  if ($[6] !== column || $[7] !== space) {
    t5 = async e_0 => {
      const ok = await column({
        space,
        ...e_0
      });
      setError(ok ? null : useData.getState().error);
      return ok;
    };
    $[6] = column;
    $[7] = space;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const edit = t5;
  let t6;
  if ($[9] !== cols || $[10] !== edit) {
    t6 = (s_1, j) => {
      const group = cols.filter(c => c.category === s_1.category && !c.hidden);
      if (j < 0 || j >= group.length || group[j].name === s_1.name) {
        return;
      }
      const rest = group.filter(c_0 => c_0.name !== s_1.name);
      edit({
        status: s_1.name,
        after: j === 0 ? null : rest[j - 1].name
      });
    };
    $[9] = cols;
    $[10] = edit;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const moveTo = t6;
  let t7;
  if ($[12] !== cols || $[13] !== moveTo) {
    t7 = (s_2, by) => {
      const group_0 = cols.filter(c_1 => c_1.category === s_2.category && !c_1.hidden);
      moveTo(s_2, group_0.findIndex(c_2 => c_2.name === s_2.name) + by);
    };
    $[12] = cols;
    $[13] = moveTo;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const move = t7;
  let t8;
  if ($[15] !== moveTo) {
    t8 = s_3 => e_1 => {
      const row = e_1.currentTarget.closest("[data-t=column-row]");
      const list = row?.parentElement;
      if (!row || !list) {
        return;
      }
      e_1.preventDefault();
      const grip = e_1.currentTarget;
      grip.setPointerCapture(e_1.pointerId);
      const rows = [...list.querySelectorAll("[data-t=column-row]:not([data-hidden])")];
      const mids = rows.map(_temp3);
      const from = rows.indexOf(row);
      const y0 = e_1.clientY;
      const h = row.getBoundingClientRect().height;
      let to = from;
      row.classList.add("dragging");
      const onMove = ev => {
        const dy = ev.clientY - y0;
        row.style.translate = `0 ${dy}px`;
        const mid = mids[from] + dy;
        to = mids.filter((m, i) => i !== from && m < mid).length;
        rows.forEach((r_0, i_0) => {
          if (r_0 === row) {
            return;
          }
          const shift = from < to && i_0 > from && i_0 <= to ? -h : from > to && i_0 >= to && i_0 < from ? h : 0;
          r_0.style.translate = shift ? `0 ${shift}px` : "";
        });
      };
      const onUp = () => {
        grip.removeEventListener("pointermove", onMove);
        grip.removeEventListener("pointerup", onUp);
        grip.removeEventListener("pointercancel", onUp);
        row.classList.remove("dragging");
        for (const r_1 of rows) {
          r_1.style.translate = "";
        }
        if (to !== from) {
          moveTo(s_3, to);
        }
      };
      grip.addEventListener("pointermove", onMove);
      grip.addEventListener("pointerup", onUp);
      grip.addEventListener("pointercancel", onUp);
    };
    $[15] = moveTo;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const drag = t8;
  let t9;
  if ($[17] !== cols || $[18] !== drag || $[19] !== edit || $[20] !== move || $[21] !== name || $[22] !== renaming || $[23] !== spaceName || $[24] !== t) {
    t9 = CATEGORIES.map(cat => {
      const rows_0 = cols.filter(c_3 => c_3.category === cat);
      if (!rows_0.length) {
        return null;
      }
      return <div key={cat}><SectionHead>{t(`columns.category.${cat}`)}</SectionHead><Group t={`columns-${cat}`}>{rows_0.map((s_4, i_1) => <Stack key={s_4.name} data-t="column-row" data-name={s_4.name} data-hidden={s_4.hidden ? "" : undefined} title={renaming === s_4.name ? <form onSubmit={e_2 => {
            e_2.preventDefault();
            if (name.trim()) {
              edit({
                status: s_4.name,
                name: name.trim()
              }).then(ok_0 => {
                if (!ok_0) {
                  return;
                }
                setRenaming(null);
                setName("");
              });
            }
          }}><Field t="column-name" aria-label={t("columns.name")} value={name} onChange={e_3 => setName(e_3.target.value)} /></form> : <span className={s_4.hidden ? "who-line muted" : "who-line"}>{s_4.own && !s_4.hidden && rows_0.filter(_temp4).length > 1 && <span className="col-grip" data-t="column-drag" role="button" aria-label={t("columns.drag")} onPointerDown={drag(s_4)}><Icon name="grip" size={14} /></span>}<Dot color={s_4.color} />{s_4.name}</span>} sub={s_4.own ? s_4.fixed ? t("columns.fixed") : null : t("columns.from", {
            space: spaceName(s_4.space)
          })} value={s_4.own ? <span className="flex gap-3">{i_1 > 0 && <LinkButton data-t="column-up" aria-label={t("columns.up")} onClick={() => move(s_4, -1)}>{"↑"}</LinkButton>}{i_1 < rows_0.length - 1 && <LinkButton data-t="column-down" aria-label={t("columns.down")} onClick={() => move(s_4, 1)}>{"↓"}</LinkButton>}{!s_4.fixed && <LinkButton data-t="column-rename" onClick={() => {
              setName(s_4.name);
              setRenaming(renaming === s_4.name ? null : s_4.name);
            }}>{t("columns.rename")}</LinkButton>}</span> : <LinkButton data-t="column-hide" onClick={() => void edit({
            status: s_4.name,
            hidden: !s_4.hidden
          })}>{s_4.hidden ? t("columns.show") : t("columns.hide")}</LinkButton>} />)}</Group></div>;
    });
    $[17] = cols;
    $[18] = drag;
    $[19] = edit;
    $[20] = move;
    $[21] = name;
    $[22] = renaming;
    $[23] = spaceName;
    $[24] = t;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== t) {
    t10 = t("columns.add");
    $[26] = t;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== t10) {
    t11 = <SectionHead>{t10}</SectionHead>;
    $[28] = t10;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== category || $[31] !== color || $[32] !== edit || $[33] !== name || $[34] !== renaming) {
    t12 = e_4 => {
      e_4.preventDefault();
      if (name.trim() && !renaming) {
        edit({
          name: name.trim(),
          category,
          color
        }).then(ok_1 => ok_1 && setName(""));
      }
    };
    $[30] = category;
    $[31] = color;
    $[32] = edit;
    $[33] = name;
    $[34] = renaming;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] !== t) {
    t13 = t("columns.name");
    $[36] = t;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  let t14;
  if ($[38] !== t) {
    t14 = t("columns.name");
    $[38] = t;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  const t15 = renaming ? "" : name;
  let t16;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = e_5 => setName(e_5.target.value);
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== name || $[42] !== renaming) {
    t17 = !name.trim() || !!renaming;
    $[41] = name;
    $[42] = renaming;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  let t18;
  if ($[44] !== t) {
    t18 = t("columns.addButton");
    $[44] = t;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  let t19;
  if ($[46] !== t17 || $[47] !== t18) {
    t19 = <LinkButton type="submit" disabled={t17}>{t18}</LinkButton>;
    $[46] = t17;
    $[47] = t18;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  let t20;
  if ($[49] !== t13 || $[50] !== t14 || $[51] !== t15 || $[52] !== t19) {
    t20 = <Field t="column-new" placeholder={t13} aria-label={t14} value={t15} onChange={t16} action={t19} />;
    $[49] = t13;
    $[50] = t14;
    $[51] = t15;
    $[52] = t19;
    $[53] = t20;
  } else {
    t20 = $[53];
  }
  let t21;
  if ($[54] !== t) {
    t21 = t("columns.categoryLabel");
    $[54] = t;
    $[55] = t21;
  } else {
    t21 = $[55];
  }
  let t22;
  if ($[56] !== category || $[57] !== t) {
    t22 = CATEGORIES.map(c_5 => <Segment key={c_5} pressed={category === c_5} onClick={() => setCategory(c_5)}>{t(`columns.category.${c_5}`)}</Segment>);
    $[56] = category;
    $[57] = t;
    $[58] = t22;
  } else {
    t22 = $[58];
  }
  let t23;
  if ($[59] !== t21 || $[60] !== t22) {
    t23 = <Segmented t="column-category" label={t21}>{t22}</Segmented>;
    $[59] = t21;
    $[60] = t22;
    $[61] = t23;
  } else {
    t23 = $[61];
  }
  let t24;
  if ($[62] !== t) {
    t24 = t("columns.color");
    $[62] = t;
    $[63] = t24;
  } else {
    t24 = $[63];
  }
  let t25;
  if ($[64] !== color) {
    t25 = SWATCHES.map(c_6 => <button key={c_6} type="button" className="swatch" role="radio" aria-checked={color === c_6} aria-label={c_6} style={{
      background: c_6
    }} onClick={() => setColor(c_6)} />);
    $[64] = color;
    $[65] = t25;
  } else {
    t25 = $[65];
  }
  const t26 = SWATCHES.includes(color) ? undefined : "";
  let t27;
  if ($[66] !== color) {
    t27 = SWATCHES.includes(color) ? undefined : {
      background: color
    };
    $[66] = color;
    $[67] = t27;
  } else {
    t27 = $[67];
  }
  let t28;
  if ($[68] !== t) {
    t28 = t("columns.color");
    $[68] = t;
    $[69] = t28;
  } else {
    t28 = $[69];
  }
  let t29;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = e_6 => setColor(e_6.target.value);
    $[70] = t29;
  } else {
    t29 = $[70];
  }
  let t30;
  if ($[71] !== color || $[72] !== t28) {
    t30 = <input type="color" data-t="column-color" aria-label={t28} value={color} onChange={t29} />;
    $[71] = color;
    $[72] = t28;
    $[73] = t30;
  } else {
    t30 = $[73];
  }
  let t31;
  if ($[74] !== t26 || $[75] !== t27 || $[76] !== t30) {
    t31 = <label className="swatch custom" data-on={t26} style={t27}>{t30}</label>;
    $[74] = t26;
    $[75] = t27;
    $[76] = t30;
    $[77] = t31;
  } else {
    t31 = $[77];
  }
  let t32;
  if ($[78] !== t24 || $[79] !== t25 || $[80] !== t31) {
    t32 = <div className="swatches" role="radiogroup" aria-label={t24}>{t25}{t31}</div>;
    $[78] = t24;
    $[79] = t25;
    $[80] = t31;
    $[81] = t32;
  } else {
    t32 = $[81];
  }
  let t33;
  if ($[82] !== t12 || $[83] !== t20 || $[84] !== t23 || $[85] !== t32) {
    t33 = <form className="col-add" onSubmit={t12}>{t20}{t23}{t32}</form>;
    $[82] = t12;
    $[83] = t20;
    $[84] = t23;
    $[85] = t32;
    $[86] = t33;
  } else {
    t33 = $[86];
  }
  let t34;
  if ($[87] !== error) {
    t34 = error && <ErrorLine>{error}</ErrorLine>;
    $[87] = error;
    $[88] = t34;
  } else {
    t34 = $[88];
  }
  let t35;
  if ($[89] !== t11 || $[90] !== t33 || $[91] !== t34 || $[92] !== t9) {
    t35 = <div data-t="columns">{t9}{t11}{t33}{t34}</div>;
    $[89] = t11;
    $[90] = t33;
    $[91] = t34;
    $[92] = t9;
    $[93] = t35;
  } else {
    t35 = $[93];
  }
  return t35;
}
function _temp4(c_4) {
  return !c_4.hidden;
}
function _temp3(r) {
  const b = r.getBoundingClientRect();
  return b.top + b.height / 2;
}
function _temp2(s_0) {
  return s_0.snapshot?.projects;
}
function _temp(s) {
  return s.column;
}